import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { parseVideoLink } from '@/features/instruments/domain/videoSource'
import videoPreviewPlayIcon from '@/assets/instruments/question-builder/video-preview-play.svg'
import { palettePrimaryMutedSurface } from '@/theme/designTokens'

export const VIDEO_PREVIEW_EMPTY_COPY = 'Enter a video link to preview here'

type Props = Readonly<{
  videoUrl: string
  testId?: string
  minHeight?: number
}>

/** Inline YouTube / Vimeo embed or MP4 player — no external navigation. */
export function VideoPlaybackEmbed({ videoUrl, testId = 'video-playback', minHeight = 140 }: Props) {
  const parsed = parseVideoLink(videoUrl)

  if (!parsed) {
    return (
      <Box
        data-testid={`${testId}-empty`}
        sx={{
          minHeight,
          borderRadius: '10px',
          bgcolor: palettePrimaryMutedSurface,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          px: 2,
        }}
      >
        <Box
          component="img"
          src={videoPreviewPlayIcon}
          alt=""
          sx={{ width: 24, height: 24, display: 'block' }}
        />
        <Typography
          sx={{
            fontSize: '14px',
            lineHeight: '21px',
            fontWeight: 500,
            letterSpacing: '-0.15px',
            color: '#6b7a90',
            textAlign: 'center',
          }}
        >
          {VIDEO_PREVIEW_EMPTY_COPY}
        </Typography>
      </Box>
    )
  }

  if (parsed.kind === 'mp4') {
    return (
      <Box
        data-testid={`${testId}-mp4`}
        sx={{
          width: '100%',
          borderRadius: '10px',
          overflow: 'hidden',
          bgcolor: '#000',
          minHeight,
        }}
      >
        <Box
          component="video"
          src={parsed.src}
          controls
          playsInline
          preload="metadata"
          sx={{ width: '100%', display: 'block', maxHeight: 360 }}
        />
      </Box>
    )
  }

  return (
    <Box
      data-testid={`${testId}-${parsed.kind}`}
      sx={{
        position: 'relative',
        width: '100%',
        pt: '56.25%',
        borderRadius: '10px',
        overflow: 'hidden',
        bgcolor: '#000',
      }}
    >
      <Box
        component="iframe"
        title="Video preview"
        src={parsed.embedUrl}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 0,
        }}
      />
    </Box>
  )
}
